import { useEffect, useState } from 'react'
import debounce from 'lodash.debounce'
import YesObject from '~/types/yesObject.type'

type UseAsyncAutocompleteProps = {
  query: string
  fetchSuggestions: (query: string) => Promise<YesObject[]>
}

export default function useAsyncAutocomplete({
  query,
  fetchSuggestions
}: UseAsyncAutocompleteProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [suggestions, setSuggestions] = useState<YesObject[]>([])

  useEffect(() => {
    if (!query) {
      setIsLoading(false)
      setSuggestions((prevSuggestions) =>
        prevSuggestions.length ? [] : prevSuggestions
      )

      return
    }

    let cleanedUp = false

    const debouncedFetchAndSet = debounce(async () => {
      setIsLoading(true)

      let newSuggestions: YesObject[] | null = null
      let newError = null

      try {
        newSuggestions = await fetchSuggestions(query)
      } catch (fetchError) {
        newError =
          fetchError instanceof Error
            ? fetchError
            : new Error((fetchError as object).toString())
      }

      if (cleanedUp) {
        return
      }

      setIsLoading(false)
      setSuggestions((prevSuggestions) => newSuggestions || prevSuggestions)
      setError(newError)
    }, 350)

    debouncedFetchAndSet()

    return () => {
      debouncedFetchAndSet.cancel()
      cleanedUp = true
    }
  }, [query, fetchSuggestions])

  return {
    isLoading,
    error,
    suggestions
  }
}
